// Audit-trail endpoint (admin JWT). Shape mirrors the API exactly:
//   - AuditRecord → api/src/services/audit-log.ts
//   - the route   → api/src/http/routes/admin.ts (GET /admin/audit?limit=1..500)
//
// Success bodies are Zod-validated at the boundary (vault Type_Assertion) so
// envelope drift fails loud as a ContractError, not a downstream undefined.
import { z } from "zod";
import { apiFetch } from "./client";

export interface AuditEntry {
  id: string;
  orgId: string;
  /** The acting admin (null = system/automated, or an actor later removed). */
  actorAdminId: string | null;
  action: string;
  /** Non-secret structured context, e.g. { channel: "sprint-42" }. */
  detail: Record<string, unknown>;
  createdAt: string;
}

const auditEntrySchema = z.object({
  id: z.string(),
  orgId: z.string(),
  actorAdminId: z.string().nullable(),
  action: z.string(),
  detail: z.record(z.string(), z.unknown()),
  createdAt: z.string(),
});

/** One page of the audit trail: the rows + an opaque keyset cursor for "load more"
 *  (null once the last page is reached). */
export interface AuditPage {
  entries: AuditEntry[];
  nextCursor: string | null;
}

const auditPageSchema = z.object({
  entries: z.array(auditEntrySchema),
  nextCursor: z.string().nullable(),
});

export interface ListAuditOptions {
  /** Page size — clamps 1..500 server-side (default 100). */
  limit?: number;
  /** Server-side filter to a single action type. */
  action?: string;
  /** Keyset cursor from the previous page's `nextCursor`. */
  before?: string;
}

/** Newest-first audit page for the caller's org (server-side filter + keyset pagination). */
export async function listAudit(opts: ListAuditOptions = {}, signal?: AbortSignal): Promise<AuditPage> {
  const params = new URLSearchParams();
  if (opts.limit) params.set("limit", String(opts.limit));
  if (opts.action) params.set("action", opts.action);
  if (opts.before) params.set("before", opts.before);
  const q = params.toString() ? `?${params.toString()}` : "";
  return apiFetch(`/admin/audit${q}`, { auth: true, signal, schema: auditPageSchema });
}
